import type { QueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { getTimeEntry, getTimeSlots } from '../../../../api/commonApi'
import type { TimeEntryLike } from '@/features/timeEntries/types'
import type { DailyTimeSlot } from '@/features/timeEntries/hooks/useDailyTimeSlots'
import { getStoredLoginUserId } from '@/utils/currentUser'
import { getFilterDateRange } from '@/features/timeEntries/utils/dateRange'

type Options = {
  queryClient: QueryClient
  userId?: string | number
  entryIds?: Array<string | number>
  deletedIds?: Array<string | number>
}

/** Reconcile only changed records, retaining unrelated rows and query scopes. */
export async function refreshTimeEntryCaches({ queryClient, userId, entryIds = [], deletedIds = [] }: Options) {
  const deleted = new Set(deletedIds.map(String))
  const ids = Array.from(new Set(entryIds.map(String))).filter(id => !deleted.has(id))
  const affected = new Set([...ids, ...Array.from(deleted)])
  if (!affected.size) return { entries: [] as TimeEntryLike[], slots: [] as DailyTimeSlot[] }
  const matches = (query: { queryKey: readonly unknown[] }) => {
    const [kind, scope] = query.queryKey as [string, { userId?: string | number }?]
    return ['daily-time-entries', 'daily-time-slots', 'weekly-time-entries'].includes(kind) &&
      String(scope?.userId ?? '') === String(userId ?? '')
  }
  await queryClient.cancelQueries({ predicate: matches })
  const loginUserId = getStoredLoginUserId()
  const delegation = userId != null && String(userId) !== String(loginUserId) ? `&delegators=${encodeURIComponent(userId)}` : ''
  const [responses, slots] = await Promise.all([
    Promise.all(ids.map(id => getTimeEntry(id))),
    ids.length ? getTimeSlots(`?timeentry_ids=${ids.join(',')}${delegation}`) : Promise.resolve([]),
  ])
  const entries: TimeEntryLike[] = responses.flatMap(response => Array.isArray(response) ? response : response ? [response] : [])
  const nextSlots = (slots || []) as DailyTimeSlot[]
  const movedSlotIds = new Set(nextSlots.map(slot => String(slot.id)))
  const dateKey = (entry: TimeEntryLike) => moment(entry.dt ?? entry.date).format('YYYY-MM-DD')
  const replaceEntries = (previous: TimeEntryLike[], incoming: TimeEntryLike[]) => {
    const byId = new Map(incoming.map(entry => [String(entry.id), entry]))
    return [
      ...previous.flatMap(entry => {
        const id = String(entry.id)
        if (!affected.has(id)) return [entry]
        const replacement = byId.get(id)
        byId.delete(id)
        return replacement ? [replacement] : []
      }),
      ...Array.from(byId.values()),
    ]
  }
  const replaceSlots = (previous: DailyTimeSlot[], incoming: DailyTimeSlot[]) => [
    ...previous.filter(slot => !affected.has(String(slot.timeentry)) && !movedSlotIds.has(String(slot.id))),
    ...incoming,
  ]
  for (const query of queryClient.getQueryCache().findAll({ predicate: matches })) {
    const [kind, scope] = query.queryKey as [string, { dateKey?: string; filter: number; step: number }]
    queryClient.setQueryData(query.queryKey, (previous: any) => {
      if (previous === undefined) return previous
      if (kind === 'daily-time-entries') {
        return replaceEntries(previous, entries.filter(entry => dateKey(entry) === scope.dateKey))
      }
      if (kind === 'daily-time-slots') {
        return replaceSlots(previous, nextSlots.filter(slot => moment(slot.start as string).format('YYYY-MM-DD') === scope.dateKey))
      }
      const { startDate, endDate } = getFilterDateRange(scope.filter, scope.step)
      const inRange = entries.filter(entry => dateKey(entry) >= startDate && dateKey(entry) <= endDate)
      const entryIdsInRange = new Set(inRange.map(entry => String(entry.id)))
      return {
        ...previous,
        timeEntries: replaceEntries(previous.timeEntries, inRange),
        timeSlots: replaceSlots(previous.timeSlots, nextSlots.filter(slot => entryIdsInRange.has(String(slot.timeentry)))),
      }
    })
  }
  void queryClient.invalidateQueries({ predicate: query => {
    const [kind, scope] = query.queryKey as [string, { userId?: string | number }?]
    return kind === 'hours-by-user' && String(scope?.userId ?? '') === String(userId ?? '')
  } })
  return { entries, slots: nextSlots }
}
